<template>
  <Teleport v-if="mounted" to="#header-breadcrumbs">
    <CBreadcrumb v-bind="{ routes }" disableGoToBack />
  </Teleport>
  <CCard class="p-4 md:p-5 mb-5">
    <header
      class="flex flex-col md:flex-row items-center justify-between gap-4"
    >
      <div class="w-full flex-y-center justify-between">
        <h2 class="text-base leading-130 font-semibold text-dark-100">
          {{ t("my_bots_count", { count: paginationContent?.total }) }}
        </h2>
        <div class="flex-y-center gap-4">
          <div class="shrink-0" v-if="activeBots?.length && isMobile">
            <div class="flex-y-center gap-2">
              <CButton
                variant="warning"
                :text="$t('delete_bot')"
                icon-position="right"
                icon="icon-trash"
                size="sm"
                class="!w-full md:!w-auto block md:hidden text-lg rounded-xl"
                @click="showDeleteDialog"
              />
              <CRefreshButton
                class="flex-shrink-0"
                @refresh-bots="refreshBots"
                :is-refreshed="isRefreshed"
              />
            </div>
          </div>
          <template
            v-if="
              user?.user_bots_count <= user?.plan?.allowed_strategies?.length &&
              user?.user_bots_count === 0
            "
          >
            <CButton
              :text="$t('add')"
              class="!max-w-full md:!max-w-[150px] !hidden md:flex-y-center justify-center"
              icon="icon-add !text-lg !leading-[18px]"
              icon-position="left"
              size="sm"
              @click="isUserVerified(checkIsActivePlan, true)"
            />
          </template>
          <CButton
            v-if="activeBots?.length"
            variant="warning"
            :text="$t('delete_bot')"
            icon-position="right"
            icon="icon-trash text-lg"
            size="sm"
            class="!w-full md:!w-auto hidden md:block"
            @click="showDeleteDialog"
          />
        </div>
      </div>
      <div
        class="w-full md:w-auto flex flex-col md:flex-row items-start flex-shrink-0 md:items-center justify-end gap-4 focus-within:border-green"
      >
        <div class="flex-shrink-0" v-if="isDesktop">
          <CRefreshButton
            @refresh-bots="refreshBots"
            :is-refreshed="isRefreshed"
          />
        </div>
        <FSelect
          :key="botStatuses"
          v-model="botStatus"
          :options="botStatuses"
          label-key="title"
          value-key="value"
          head-styles="!h-10"
          :loading="loading"
          :placeholder="$t('choose_region')"
          class="w-full md:w-auto min-w-max"
        />
        <FInput
          v-model.trim="search"
          :placeholder="$t('search')"
          class="w-full !h-10 !min-w-[250px] lg:max-w-[250px]"
          suffix-class="pl-2"
          prefix-class="text-xl text-gray-450 font-semibold flex items-center mr-2 leading-130"
        >
          <template #prefix>
            <i class="icon-search-normal" />
          </template>
          <template #suffix>
            <button
              @click="search = ''"
              :class="[!search ? 'opacity-0 invisible' : 'opacity-100 visible']"
              class="w-5 h-5 flex-center bg-gray/[16%] rounded-full p-1 transition-200 group hover:bg-red"
            >
              <span
                class="icon-close text-gray-450 text-[10px] transition-200 group-hover:text-white"
              ></span>
            </button>
          </template>
        </FInput>
        <template
          v-if="
            user?.user_bots_count <= user?.plan?.allowed_strategies?.length &&
            user?.user_bots_count === 0
          "
        >
          <CButton
            :text="$t('add')"
            class="!max-w-full md:!max-w-[150px]"
            icon="icon-add !text-lg !leading-[18px]"
            icon-position="left"
            size="sm"
            @click="isUserVerified(checkIsActivePlan, true)"
          />
        </template>
      </div>
    </header>
  </CCard>

  <Transition name="fade" mode="out-in">
    <div :key="loading || deletedListLoading">
      <div
        v-if="loading || deletedListLoading"
        class="grid lg:grid-cols-2 xl:grid-cols-3 gap-4"
      >
        <BotCardShimmer v-for="el of 5" :key="el" />
      </div>
      <div
        v-else-if="
          (activePaginationData.total > 0 && botList?.length) ||
          (deletedPaginationData.total > 0 && botList?.length)
        "
        class="grid lg:grid-cols-2 xl:grid-cols-3 gap-4"
      >
        <CBotCard
          v-for="(bot, idx) in botList"
          :key="idx"
          v-bind="{ bot }"
          @show-plan="showModal = true"
        />
      </div>
      <CNodata
        v-else
        main-class="min-h-[305px]"
        image="/images/svg/no-data/no-bots2.svg"
        :title="
          botStatus === 'active_bots'
            ? $t('no_bots')
            : $t('no_deleted_bots_title')
        "
        :subtitle="
          botStatus === 'active_bots'
            ? $t('no_bots_text')
            : $t('no_deleted_bots_subtitle')
        "
        top-main-class="bg-white rounded-xl"
        :button-text="botStatus === 'active_bots' ? $t('add_bot') : ''"
        @submit="isUserVerified(checkIsActivePlan, true)"
        custom-button-class="max-w-[170px] text-sm"
      />
      <CTableFooter
        class="z-10 relative"
        :total="paginationContent.total"
        :limit="paginationContent.defaultLimit"
        :items-per-page="paginationContent.defaultLimit"
        :current-page="paginationContent.currentPage"
        @pageChange="paginationPageChangeActions"
        @itemsPerPage="paginationLimitChangeActions"
      />
    </div>
  </Transition>
  <CModalStatus @fetch-data="fetchActiveBots" />
  <CPlanDialog :show="showModal" @close="showModal = false" />
  <BotCreate
    :show-bot-create="showCreateBotValue"
    @closeBotCreateModal="showCreateBotValue = false"
    @created="newBotCreated"
  />
  <CDeleteDialog
    :show="showDelete"
    variant="warning"
    :title="$t('bot_delete')"
    :subtitle="$t('bot_delete_text')"
    :loading="isLoading"
    @close="showDelete = false"
    @submit="deleteBot()"
  />
</template>

<script setup lang="ts">
import CDeleteDialog from "@/components/Common/Dialog/CDeleteDialog.vue";
import CBreadcrumb from "@/components/Common/CBreadcrumb.vue";
import { useMounted } from "@/composables/useMounted";
import { useI18n } from "vue-i18n";
import { computed, onMounted, ref, watch } from "vue";
import CCard from "@/components/Card/CardWrapper.vue";
import { useTableFetch } from "@/composables/useTableFetch";
import CButton from "@/components/Common/CButton.vue";
import CNodata from "@/components/Common/CNodata.vue";
import CPlanDialog from "@/components/Common/Dialog/CPlanDialog.vue";
import { isUserVerified } from "@/composables/useUserVerified";
import CModalStatus from "@/modules/Bots/components/CModalStatus.vue";
import CBotCard from "@/modules/Bots/components/CBotCard.vue";
import { useAuthStore } from "@/modules/Auth/stores";
import BotCardShimmer from "@/components/Shimmer/BotCardShimmer.vue";
import ApiService from "@/services/ApiService";
import FInput from "@/components/Form/Input/FInput.vue";
import CRefreshButton from "@/components/Common/CRefreshButton.vue";
import { useMobile } from "@/composables/useMobile";
import FSelect from "@/components/Form/Select/FSelect.vue";
import CTableFooter from "@/components/Common/Table/CTableFooter.vue";
import { useCustomToast } from "@/composables/useCustomToast";
import BotCreate from "@/modules/Bots/components/BotCreate.vue";

import { usePushToDataLayer } from "@/composables/usePushToDataLayer";
const { pushToDataLayer } = usePushToDataLayer();

const { mounted } = useMounted();
const { t } = useI18n();
const { showToast } = useCustomToast();

const { isMobile } = useMobile("mobile");
const { isDesktop } = useMobile("desktop");

const store = useAuthStore();

const search = ref("");
const botStatus = ref("active_bots");
const showModal = ref(false);
const showCreateBotValue = ref(false);

const showDelete = ref(false);
const isLoading = ref(false);

const botStatuses = computed(() => [
  {
    title: t("active_bots"),
    value: "active_bots",
  },
  {
    title: t("deleted_bots"),
    value: "deleted_bots",
  },
]);
const botList = computed(() => {
  return botStatus.value === "active_bots"
    ? activeBots.value
    : deletedBots.value;
});

const paginationContent = computed(() => {
  return botStatus.value === "active_bots"
    ? activePaginationData
    : deletedPaginationData;
});

const paginationPageChangeActions = computed(() => {
  return botStatus.value === "active_bots"
    ? activeBotsPageChange
    : deleteBotsPageChange;
});
const paginationLimitChangeActions = computed(() => {
  return botStatus.value === "active_bots"
    ? activeBotsLimitChange
    : deleteBotsLimitChange;
});
const routes = computed(() => [
  {
    name: t("my_bots"),
    route: "/bots",
  },
]);
const user = computed(() => store.user);
const isRefreshed = ref(false);

const showDeleteDialog = () => {
  showDelete.value = true;
};
const deleteBot = () => {
  if (allBotIDs.value?.length) {
    isLoading.value = true;
    ApiService.delete("/bot/delete/", { bot_ids: allBotIDs.value })
      .then(() => {
        useAuthStore().fetchUserData();
        fetchActiveBots();
      })
      .catch(() => {
        showToast(t("error"), "error");
      })
      .finally(() => {
        showDelete.value = false;
        isLoading.value = false;
      });
  }
};
const newBotCreated = async () => {
  await store.fetchUserData();
  await fetchActiveBots();
};

const refreshBots = () => {
  isRefreshed.value = true;
  ApiService.post("/bot/refresh/")
    .then(async () => {
      await fetchActiveBots();
      await fetchDeletedBots();
      pushToDataLayer("refresh_bot_success", {
        event_category: "refresh_bot",
        event_label: "refresh_bot_success",
        button_text: "Refresh Bot",
      });
    })
    .catch((err: any) => {
      console.log(err);
    })
    .finally(() => {
      setTimeout(() => {
        isRefreshed.value = false;
      }, 300);
    });
};

const checkIsActivePlan = () => {
  const planTitle = user.value?.plan;

  if (planTitle !== null && planTitle !== "") {
    showCreateBotValue.value = true;
  } else {
    showModal.value = true;
  }
};

const {
  tableData: activeBots,
  paginationData: activePaginationData,
  onSearch,
  loading,
  onPageChange: activeBotsPageChange,
  onChangeLimit: activeBotsLimitChange,
  fetchTableData: fetchActiveBots,
} = useTableFetch(`/bot/`, {
  limit: 50,
  offset: 50,
  size: 12,
});
const { tableData: allBots } = useTableFetch(`/bot/`, {
  page: 1,
  size: 150,
});
const allBotIDs = computed(() => allBots.value.map((el) => el?.id));

const {
  tableData: deletedBots,
  paginationData: deletedPaginationData,
  loading: deletedListLoading,
  onPageChange: deleteBotsPageChange,
  onChangeLimit: deleteBotsLimitChange,
  fetchTableData: fetchDeletedBots,
} = useTableFetch("/bot/deleted/");

onMounted(() => {
  store.fetchUserData();
  pushToDataLayer("pageview", {
    page: "bots",
    event_category: "page",
    event_label: "bots_page_view",
    button_text: "None",
  });
});
watch(
  () => search.value,
  (val) => {
    onSearch(val);
  },
);
watch(
  () => botStatus.value,
  (val) => {
    if (val === "active_bots") {
      fetchActiveBots();
    } else {
      fetchDeletedBots();
    }
  },
);
</script>
<style>
.refresh {
  animation: spinIt 0.8s infinite linear !important;
}
@keyframes spinIt {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
